import { useEffect, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { Button } from "~/shared/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "~/shared/ui/dialog";
import { Input } from "~/shared/ui/input";
import { Label } from "~/shared/ui/label";
import { EDITABLE_FIELD_CLASS } from "~/shared/lib/field-classes";
import { SuperadminAttributionFields } from "~/features/superadmin-attribution";

import { useRenameChat } from "~/entities/chat/chat-mutations";

const TITLE_MAX = 255;

interface RenameChatDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    chatId: string;
    workspaceId?: string;
    currentTitle: string;
}

export function RenameChatDialog({
    open,
    onOpenChange,
    chatId,
    workspaceId,
    currentTitle,
}: RenameChatDialogProps) {
    const { t } = useTranslation("chat");
    const [value, setValue] = useState(currentTitle);
    const inputRef = useRef<HTMLInputElement>(null);
    const renameChat = useRenameChat();

    const mutationRef = useRef(renameChat);
    mutationRef.current = renameChat;

    useEffect(() => {
        if (open) {
            setValue(currentTitle);
            mutationRef.current.reset();
            requestAnimationFrame(() => inputRef.current?.select());
        }
    }, [open, currentTitle]);

    const trimmed = value.trim();
    const original = currentTitle.trim();
    const canSubmit =
        trimmed.length > 0 && trimmed.length <= TITLE_MAX && trimmed !== original && !renameChat.isPending;

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!canSubmit) return;
        try {
            await renameChat.mutateAsync({ chatId, title: trimmed, workspaceId });
            onOpenChange(false);
        } catch (error) {
            const fallback = t("header.renameDialog.errorGeneric");
            toast.error(error instanceof Error && error.message ? error.message : fallback);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <form onSubmit={handleSubmit} className="grid gap-4">
                    <DialogHeader>
                        <DialogTitle>{t("header.renameDialog.title")}</DialogTitle>
                        <DialogDescription>{t("header.renameDialog.description")}</DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-2">
                        <Label htmlFor="rename-chat-title">{t("header.renameDialog.nameLabel")}</Label>
                        <Input
                            id="rename-chat-title"
                            ref={inputRef}
                            value={value}
                            maxLength={TITLE_MAX}
                            placeholder={t("header.renameDialog.placeholder")}
                            onChange={(e) => setValue(e.target.value)}
                            className={EDITABLE_FIELD_CLASS}
                        />
                    </div>
                    <SuperadminAttributionFields entity="chats" entityId={chatId} />
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            disabled={renameChat.isPending}
                        >
                            {t("header.renameDialog.cancel")}
                        </Button>
                        <Button type="submit" variant="accent" disabled={!canSubmit}>
                            {renameChat.isPending
                                ? t("header.renameDialog.submitting")
                                : t("header.renameDialog.save")}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
